// Component for designing individual survey questions
// Allows users to create and configure different types of questions
import React, { useState } from 'react';
import { FileEdit } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

function QuestionDesigner({ onAddQuestion }) {
  // State management for question creation
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState('short-text');
  const [error, setError] = useState('');
  const [options, setOptions] = useState(['']);

  // Available question types
  const questionTypes = [
    { value: 'short-text', label: 'Short Text' },
    { value: 'long-text', label: 'Long Text' },
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'checkbox-list', label: 'Checkbox List' },
    { value: 'numeric', label: 'Numeric Input' }    
  ];

  // Handle form submission for adding a new question
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation: Check for empty question text
    if (!questionText.trim()) {
      setError('Question text cannot be empty');
      return;
    }

    // Validation: Check for minimum options in multiple choice questions
    if ((questionType === 'multiple-choice' || questionType === 'checkbox-list') && 
        options.filter(opt => opt.trim()).length < 2) {
      setError('Please add at least 2 options');
      return;
    }

    // Create and add the new question
    onAddQuestion({ 
      id: uuidv4(),
      text: questionText,
      type: questionType,
      options: (questionType === 'multiple-choice' || questionType === 'checkbox-list') 
        ? options.filter(opt => opt.trim()) 
        : undefined
    });

    // Reset form after successful submission
    setQuestionText('');
    setOptions(['']);
    setError('');
  };

  // Handlers for managing multiple choice options
  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
    setError('');
  };

  const addOption = () => {
    setOptions([...options, '']);
  };

  const removeOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 sticky top-4">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <FileEdit className="w-6 h-6" />
        Question Designer
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">

      <div>
          <label htmlFor="questionType" className="block text-sm font-medium text-gray-700 mb-2">
            Question Type:
          </label>
          <select
            id="questionType"
            value={questionType}
            onChange={(e) => setQuestionType(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            {questionTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>


        <div className="form-group">
          <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 mb-2">
            Question Text:
          </label>
          <textarea
            id="questionText"
            value={questionText}
            onChange={(e) => {
              setQuestionText(e.target.value);
              setError('');
            }}
            className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${error ? 'border-red-500' : ''}`}
          />
          {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
        </div>

        

        {(questionType === 'multiple-choice' || questionType === 'checkbox-list') && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options:
            </label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                  {options.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addOption}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                + Add Option
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Question
          </button>
        </div>
      </form>
    </div>
  );
}

export default QuestionDesigner;